// Proxijob — persistent WebSocket client with auto-reconnect
import { log } from "./logger";

let ws = null;
let listeners = new Set();
let reconnectTimer = null;
let pingTimer = null;
let intentionallyClosed = false;

function wsUrl() {
  const raw = process.env.REACT_APP_BACKEND_URL || "";
  const httpBase = raw.replace(/\/$/, "");
  const wsBase = httpBase.replace(/^http/, "ws");
  // Same rationale as lib/api.js: bearer JWT lives in localStorage per Emergent
  // Auth playbook. WS handshake can't send httpOnly cookies for the FastAPI
  // route, so we pass the token as a query param over TLS.
  const token = localStorage.getItem("proxijob_token") || "";
  return `${wsBase}/api/ws?token=${encodeURIComponent(token)}`;
}

function connect() {
  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;
  intentionallyClosed = false;
  try {
    ws = new WebSocket(wsUrl());
  } catch (e) {
    // Browser rejected the URL (rare — bad scheme). Try again shortly.
    log.warn("[ws] constructor failed:", e?.message || e);
    scheduleReconnect();
    return;
  }
  ws.onopen = () => {
    if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
    if (pingTimer) clearInterval(pingTimer);
    pingTimer = setInterval(() => {
      // Ping is a keep-alive best-effort; ignore transient send failures.
      try { ws?.readyState === 1 && ws.send(JSON.stringify({ type: "ping" })); }
      catch (e) { log.warn("[ws] ping failed:", e?.message || e); }
    }, 30000);
    listeners.forEach((l) => {
      // Isolate listener failures so one bad subscriber can't kill the WS.
      try { l({ type: "__open__" }); } catch (e) { log.warn("[ws] listener open error:", e?.message || e); }
    });
  };
  ws.onmessage = (e) => {
    let data;
    try { data = JSON.parse(e.data); }
    catch (err) { log.warn("[ws] bad JSON frame dropped:", err?.message || err); return; }
    listeners.forEach((l) => {
      try { l(data); } catch (err) { log.warn("[ws] listener message error:", err?.message || err); }
    });
  };
  ws.onclose = () => {
    if (pingTimer) { clearInterval(pingTimer); pingTimer = null; }
    ws = null;
    if (!intentionallyClosed) scheduleReconnect();
  };
  ws.onerror = () => {
    // The browser fires "error" then "close"; letting close() run is enough,
    // but nudging it forward keeps reconnects tight on flaky networks.
    try { ws?.close(); } catch (e) { log.warn("[ws] close after error failed:", e?.message || e); }
  };
}

function scheduleReconnect() {
  if (reconnectTimer) return;
  reconnectTimer = setTimeout(() => { reconnectTimer = null; connect(); }, 3000);
}

export function subscribeWS(listener) {
  listeners.add(listener);
  connect();
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      intentionallyClosed = true;
      try { ws?.close(); } catch (e) { log.warn("[ws] teardown close failed:", e?.message || e); }
      ws = null;
      if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
      if (pingTimer) { clearInterval(pingTimer); pingTimer = null; }
    }
  };
}

export function sendWS(payload) {
  try {
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(payload));
  } catch (e) {
    log.warn("[ws] send failed:", e?.message || e);
  }
}

export function wsReady() {
  return !!ws && ws.readyState === WebSocket.OPEN;
}
